import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { IconButton } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import SydButton from '../../../../commonDesign/Button'
import Icon from '../../../../atoms/Icon'
import { buildOrderedTree } from './util/buildOrderedTree'

const ACTION_LABELS = {
  invokeLambda: 'Lambda',
  slack: 'Slack',
  pass: 'Pass',
  fail: 'Fail',
  start: 'Start',
  workflow: 'Workflow'
}

const useStyles = makeStyles({
  container: {
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    overflow: 'hidden'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    '& th': {
      textAlign: 'left',
      fontSize: 11,
      fontWeight: 600,
      color: '#757575',
      textTransform: 'uppercase',
      padding: '10px 12px',
      borderBottom: '1px solid #E0E0E0',
      backgroundColor: '#FAFAFA'
    },
    '& td': {
      fontSize: 13,
      padding: '10px 12px',
      borderBottom: '1px solid #F0F0F0',
      verticalAlign: 'middle'
    },
    '& tr:last-child td': {
      borderBottom: 'none'
    }
  },
  row: {
    cursor: 'pointer',
    transition: 'background-color 0.1s',
    '&:hover': {
      backgroundColor: '#F5F8FF'
    }
  },
  selectedRow: {
    backgroundColor: '#E3F2FD !important'
  },
  stepName: {
    fontWeight: 600
  },
  outlineNum: {
    fontSize: 12,
    color: '#9E9E9E',
    fontFamily: 'monospace',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    width: 60,
    minWidth: 60
  },
  asyncBadge: {
    fontSize: 9,
    color: '#FFFFFF',
    backgroundColor: '#FF9800',
    borderRadius: 3,
    padding: '1px 4px',
    fontWeight: 600
  },
  actionsCell: {
    width: 40,
    textAlign: 'center'
  },
  footer: {
    padding: '12px',
    borderTop: '1px solid #E0E0E0',
    backgroundColor: '#FAFAFA'
  },
  identifier: {
    fontSize: 11,
    color: '#9E9E9E',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 200,
    display: 'block'
  }
})

function shortenIdentifier (id) {
  if (!id) return null
  const fnMatch = id.match(/:function:(.+)$/)
  return fnMatch ? fnMatch[1] : id
}

function StepListView ({ steps, selectedStepId, onSelectStep, onRemoveStep, onAddStep }) {
  const classes = useStyles()
  const editable = !!(onRemoveStep && onAddStep)

  const orderedSteps = useMemo(() => buildOrderedTree(steps), [steps])

  const handleRowClick = useCallback((dagStepId) => {
    onSelectStep(dagStepId)
  }, [onSelectStep])

  const handleDelete = useCallback((e, dagStepId) => {
    e.stopPropagation()
    onRemoveStep(dagStepId)
  }, [onRemoveStep])

  const handleAdd = useCallback(() => {
    const lastStep = steps[steps.length - 1]
    const idx = steps.length
    onAddStep({
      dagStepId: `step_${idx}`,
      name: `Step ${idx}`,
      targetAction: 'pass',
      parentStepId: lastStep?.dagStepId ?? null,
      configuration: {}
    })
  }, [steps, onAddStep])

  return (
    <div className={classes.container}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Action</th>
            <th>Target</th>
            <th>Async</th>
            {editable ? <th className={classes.actionsCell} /> : null}
          </tr>
        </thead>
        <tbody>
          {orderedSteps.map(({ step, outline }) => (
            <tr
              key={step.dagStepId}
              className={`${classes.row} ${selectedStepId === step.dagStepId ? classes.selectedRow : ''}`}
              onClick={() => handleRowClick(step.dagStepId)}
            >
              <td className={classes.outlineNum}>{outline}</td>
              <td>
                <span className={classes.stepName}>{step.name}</span>
                <span className={classes.identifier}>{step.dagStepId}</span>
              </td>
              <td>{ACTION_LABELS[step.targetAction] ?? step.targetAction}</td>
              <td>
                <span
                  className={classes.identifier}
                  title={step.targetIdentifier}
                >
                  {shortenIdentifier(step.targetIdentifier) || step.target || '\u2014'}
                </span>
              </td>
              <td>{step.isAsync ? <span className={classes.asyncBadge}>ASYNC</span> : '\u2014'}</td>
              {editable ? (
                <td className={classes.actionsCell}>
                  <IconButton
                    size='small'
                    onClick={(e) => handleDelete(e, step.dagStepId)}
                    aria-label='delete step'
                  >
                    <Icon name='delete' customSize='1rem' />
                  </IconButton>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      {editable ? (
        <div className={classes.footer}>
          <SydButton size='sm' variant='outline' icon='add' onClick={handleAdd}>
            Add Step
          </SydButton>
        </div>
      ) : null}
    </div>
  )
}

StepListView.propTypes = {
  steps: PropTypes.array.isRequired,
  selectedStepId: PropTypes.string,
  onSelectStep: PropTypes.func.isRequired,
  onRemoveStep: PropTypes.func,
  onAddStep: PropTypes.func
}

export default StepListView
